import { Link } from "react-router-dom";
import { SERVICE_SLUGS } from "@/site/serviceSlugs";
import { useT, useLangPath } from "@/i18n";
import type { Dict } from "@/i18n/types";

/** Un serviciu. Cardul e propriul component fiindcă fiecare are alt tip
 *  preselectat în formular, iar hook-urile nu se pot chema într-un map.
 *
 *  Două lucruri au ieșit din card.
 *
 *  Numărul: „01" … „06" nu codificau nimic — serviciile n-au ordine, nimeni nu
 *  cumpără serviciul 04 după 03. Pe landing se adunau la 22 de numere în cinci
 *  secvențe care reporneau.
 *
 *  Chip-urile de tehnologii: scoase. „React · Node · Postgres · nginx" e o
 *  listă despre uneltele mele, pusă în secțiunea care ar trebui să răspundă la
 *  problema cuiva. Cine cumpără un magazin cu plăți nu alege după framework, iar
 *  cine chiar vrea stiva o vede pe pagina fiecărui caz, unde e legată de un
 *  lucru livrat, nu de o promisiune.
 *
 *  Butonul: fiecare card avea „Start a project →", deci pe o singură pagină
 *  scria de șase ori exact același lucru — cea mai vizibilă repetiție de pe
 *  site. Acum ținta e cardul întreg, iar afordanța vizibilă e o săgeată. Numele
 *  accesibil rămâne distinct pentru fiecare card: un cititor de ecran care
 *  parcurge lista de butoane aude șase ținte diferite, nu „Start a project" de
 *  șase ori. Butonul plin rămâne în două locuri pe pagină — sub hero și lângă
 *  formular. */
function Service({
  id, title, p, items, level, slug,
}: Dict["home"]["services"][number] & { level: 2 | 3; slug: string }) {
  const t = useT();
  const lp = useLangPath();
  const H = level === 2 ? "h2" : "h3";
  return (
    <article className="cap" id={id}>
      {/* Pe landing serviciile stau sub un h2 de secțiune, deci sunt h3. Pe
          /services stau direct sub h1-ul paginii, deci urcă la h2 — altfel
          pagina mergea h1 → h3 → h2, adică titluri în ordine descrescătoare
          urmate de unul mai mare. */}
      {/* Spațiul dinaintea lui <br> nu se vede pe ecran lat — se colapsează
          lângă rupere — dar există în text, iar sub 900px, unde ruperea se
          scoate, el e cel care ține „Data &" lipit de „dashboards". */}
      <H>{title[0]}{" "}<br className="capbr" />{title[1]}<span className="capar" aria-hidden="true">→</span></H>
      <div className="capbody">
        <p>{p}</p>
        <ul className="capitems">
          {items.map((it) => <li key={it}>{it}</li>)}
        </ul>
      </div>
      {/* Întins peste tot rândul. Textul rămâne dedesubt și selectabil: linkul
          e transparent și n-are conținut propriu. Săgeata stă în fluxul
          titlului, nu în colț — o săgeată într-un colț e un buton deghizat.

          Duce la pagina serviciului, nu direct în formular: cine se uită la
          grilă compară, iar cine compară are întrebări înainte să scrie. Butonul
          de start stă pe pagina aceea, cu tipul de proiect deja ales. */}
      <Link className="capgo" to={lp(`/services/${slug}`)}>
        <span className="sr-only">{`${t.serviceMore} — ${title.join(" ")}`}</span>
      </Link>
    </article>
  );
}

/** Grila celor șase, folosită și pe landing și pe /services. */
export function ServiceCards({ level = 3 }: { level?: 2 | 3 } = {}) {
  const t = useT();
  return (
    <div className="caps">
      {/* Ordinea grilei e ordinea din SERVICE_SLUGS — aceleași șase, în aceeași
          succesiune. Perechea se face pe poziție fiindcă slugul e o bucată de
          adresă, care nu se traduce, iar dicționarele se traduc. */}
      {t.home.services.map((c, i) => (
        <Service key={i} {...c} level={level} slug={SERVICE_SLUGS[i]} />
      ))}
    </div>
  );
}
